import { useState, useEffect } from 'react';
import { useSEO, generateOrganizationSchema, generateWebSiteSchema } from '../../utils/seo';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LocalNewsSection from './components/LocalNewsSection';
import ThisWeekHighlights from './components/ThisWeekHighlights';
import RosevilleAbout from './components/RocketCity';
import ActivitiesGrid from './components/ActivitiesGrid';
import FeaturedRestaurants from './components/FeaturedRestaurants';
import InteractiveMap from './components/InteractiveMap';
import FoodDrinkSection from './components/FoodDrinkSection';
import GovernmentServices from './components/GovernmentServices';
import TransportationGuide from './components/TransportationGuide';
import FeaturedEvents from './components/FeaturedEvents';
import EventBanner from './components/EventBanner';
import BlogSection from './components/BlogSection';
import ResidentTestimonials from './components/ResidentTestimonials';
import SubmitTestimonial from './components/SubmitTestimonial';
import LocalBusinessSpotlight from './components/LocalBusinessSpotlight';
import WhyListWithUs from './components/WhyListWithUs';
import NeighborhoodGuide from './components/NeighborhoodGuide';
import RosevilleFAQ from './components/RosevilleFAQ';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';
import CTAButtons from './components/CTAButtons';
import Footer from './components/Footer';
import AdDisplay from '../../components/AdDisplay';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useSEO({
    title: 'Roseville CA Guide - Best Restaurants, Events, Activities & Real Estate | Roseville.org',
    description: 'Your complete guide to Roseville California. Discover the best restaurants in Roseville CA, local events, things to do, real estate listings, parks, schools, and businesses. Moving to Roseville? Start here.',
    keywords: 'Roseville CA, Roseville California, best restaurants in Roseville CA, things to do in Roseville, moving to Roseville California, Roseville CA real estate, Roseville events, Roseville businesses, Roseville parks, Roseville schools',
    ogImage: 'https://public.readdy.ai/ai/img_res/52dfbb2bef5261b5a3e91714ceb9b39e.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateWebSiteSchema(),
        {
          '@type': 'LocalBusiness',
          '@id': 'https://roseville.org/#localbusiness',
          name: 'Roseville.org — Roseville CA Community Guide',
          alternateName: 'Roseville California Guide',
          description: 'The #1 local resource for Roseville, California. Find the best restaurants in Roseville CA, upcoming events, things to do, local businesses, real estate, parks, schools, and relocation guides.',
          url: 'https://roseville.org',
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/daa4d1286c7e40add692fc5e2a0b9f5f/85b7f1e80330d554f03850e83485dc2a.jpeg',
            width: 300,
            height: 60,
          },
          image: 'https://readdy.ai/api/search-image?query=Beautiful%20aerial%20view%20of%20Roseville%20California%20cityscape%20with%20parks%20downtown%20area%20and%20residential%20neighborhoods%20under%20clear%20blue%20sky%20vibrant%20community%20atmosphere%20modern%20suburban%20development&width=1200&height=630&seq=roseville-og-home&orientation=landscape',
          telephone: '+1-916-238-8515',
          email: 'info@roseville.org',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5098 Foothills Blvd, Suite 3-402',
            addressLocality: 'Roseville',
            addressRegion: 'CA',
            postalCode: '95747',
            addressCountry: 'US',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 38.7521,
            longitude: -121.2880,
          },
          hasMap: 'https://maps.google.com/?q=Roseville,CA',
          areaServed: [
            { '@type': 'City', name: 'Roseville', '@id': 'https://en.wikipedia.org/wiki/Roseville,_California' },
            { '@type': 'City', name: 'Rocklin' },
            { '@type': 'City', name: 'Lincoln' },
            { '@type': 'City', name: 'Granite Bay' },
            { '@type': 'City', name: 'Citrus Heights' },
            { '@type': 'AdministrativeArea', name: 'Placer County', addressRegion: 'CA' },
          ],
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '08:00',
              closes: '18:00',
            },
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Saturday'],
              opens: '09:00',
              closes: '17:00',
            },
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Sunday'],
              opens: '10:00',
              closes: '16:00',
            },
          ],
          priceRange: 'Free',
          currenciesAccepted: 'USD',
          paymentAccepted: 'Cash, Credit Card',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            reviewCount: '312',
            bestRating: '5',
            worstRating: '1',
          },
          sameAs: [
            'https://www.facebook.com/RosevilleOrg',
            'https://twitter.com/RosevilleOrg',
            'https://www.instagram.com/rosevilleorg',
            'https://www.linkedin.com/company/roseville-org',
            'https://www.yelp.com/biz/roseville-org',
          ],
          knowsAbout: [
            'Roseville CA restaurants',
            'Roseville California events',
            'Things to do in Roseville CA',
            'Roseville CA real estate',
            'Moving to Roseville California',
            'Roseville CA businesses',
            'Roseville parks and recreation',
            'Roseville CA schools',
            'Roseville neighborhoods',
            'Roseville CA local news',
          ],
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Roseville.org Services',
            itemListElement: [
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Local Business Directory',
                  description: 'Comprehensive directory of Roseville CA businesses with reviews, hours, and contact info',
                  url: 'https://roseville.org/directory',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Roseville Events Calendar',
                  description: 'Complete calendar of events happening in Roseville CA',
                  url: 'https://roseville.org/calendar',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Roseville Relocation Guide',
                  description: 'Everything you need to know about moving to Roseville California',
                  url: 'https://roseville.org/relocation',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Roseville Real Estate Listings',
                  description: 'Browse homes for sale and rent in Roseville CA',
                  url: 'https://roseville.org/real-estate',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Roseville Local News',
                  description: 'Latest news and updates from Roseville California',
                  url: 'https://roseville.org/news',
                },
              },
            ],
          },
        },
        {
          '@type': 'WebPage',
          '@id': 'https://roseville.org/#webpage',
          url: 'https://roseville.org',
          name: 'Roseville CA Guide — Restaurants, Events, Activities & Real Estate',
          isPartOf: { '@id': 'https://roseville.org/#website' },
          about: { '@id': 'https://roseville.org/#localbusiness' },
          description: 'Your complete guide to Roseville California. Discover the best restaurants, local events, things to do, real estate, parks, schools, and businesses.',
          inLanguage: 'en-US',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://roseville.org' },
            ],
          },
          speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: ['h1', 'h2', '.hero-description'],
          },
        },
      ],
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar isScrolled={isScrolled} />
      <Hero />
      
      {/* Homepage Hero Ad */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdDisplay placementId="homepage-hero" />
      </div>
      
      <LocalNewsSection />
      <ThisWeekHighlights />
      <RosevilleAbout />
      <ActivitiesGrid />
      <FeaturedRestaurants />
      <InteractiveMap />
      <FoodDrinkSection />
      <GovernmentServices />
      <TransportationGuide />
      <FeaturedEvents />
      <EventBanner />
      <BlogSection />
      <ResidentTestimonials />
      <SubmitTestimonial />
      <LocalBusinessSpotlight />
      <WhyListWithUs />
      <NeighborhoodGuide />
      <RosevilleFAQ />
      <CTAButtons />
      <MailchimpNewsletter />
      <Footer />
    </div>
  );
}